'use strict';
/* ==========================================================================
   levels/song.js — "Race to 270", step 4: TWO SEVENTY (DESIGN.md §5.5; content/song.json), orientation "any".
   The karaoke of the one ACE-Step take: assets/music/song.m4a (lesson.json preload.stream: played through the shared
   <audio> element, never decoded) + assets/music/song_words.json (tools/song_timing.py: the TRUE lyrics aligned; only
   these words are shown). The take sings an extra half chorus after the last chorus that is not on the sheet: its
   words are the file's `extras` (repeats of chorus lines, word times from Whisper) and the karaoke shows them at their
   times like any line; without extras the last sung line simply stays up until the outro (nothing is invented).

   Behind it, a made-up election night on the kit's map (core-local/mapkit.js), from the model (College):
     - the final map is College.tie() (2020 census, 269–269) with Florida moved to the Lantern ticket: 299–239;
     - the other 50 jurisdictions are inked one per two beats of the take's own beat grid from the first verse word,
       the ticket that is BEHIND going next (a race); each ticket's states in a fixed shuffled order; Lantern stops at
       269, one short ("Nobody gets there?");
     - Florida inks on the first word of the LAST chorus and Lantern crosses 270; CERTIFIED lands after that chorus;
     - the map turns into the tile cartogram (one square per elector) on "Every state has its House seats, plus two"
       and again for "The census counts the people…" (back to the map before the chorus / "Forty-eight states…");
     - the brass counter rolls each state's electoral votes in over 0.45 s of SONG time.
   Nothing here hard-codes a time: every cue is a line / section of song_words.json or the beat grid.
   The whole frame is a pure function of SONG TIME t (the <audio> clock while it plays, a wall clock while audio is
   locked / missing or at __timeScale > 1), so seeking, pausing and stills are exact (the "type block" lift of a state
   just inked is the kit's own 0.75 s wall-clock flourish, only while playing).

   Controls: click / Space play-pause, ↺ restart (R), ⌂ map, ← → seek 5 s, click or drag the strip at the bottom.
   "Finish" appears once ≥ 90% of the song has been heard; the end of a listen with ≥ 90% heard stamps the step.
   Portrait phone: the picture on the stage, Dock.karaoke + ↺ ▶ ⌂ + the strip below.
   Missing timings: the lyric sheet of content/song.json and a Finish button (Enter).

   QA hooks: sc.song ({dur, lines, beats, sec}), sc.plan ({order, tFL, tStamp, windows, final}), sc.debugAt(t), sc.now(),
   sc.play(t), sc.pause(), sc.toggle(), sc.seek(t), sc.restart(), sc.done(), sc.finish(), sc.canFinish(), sc.lyricAt(t),
   sc.activeWord(t), sc.pictureAt(t) ({k, lantern, anchor, shown: {lantern, anchor}, u, stamp, winner}), sc.heard,
   sc.ended, sc.audioReady; Levels.song.plan(song), Levels.song.GEOM.
   ========================================================================== */
(function () {
  const STEP = 'song';
  const AUDIO = 'music/song.m4a', WORDS = 'music/song_words.json', KEY = 'song:electoral';
  const FINISH_AT = 0.9, ROLL = 0.45, MORPH = 1.1;
  const CREDIT = 'AI-generated song · the numbers are real';
  const NOTE = 'A made-up race: the Lantern and Anchor tickets, real electoral votes (2020 census).';
  const NOTE_TILES = 'One square per elector (2020 census).';
  const WOOD = "'Alfa Slab One', Georgia, serif", TYPE = "'Special Elite', 'Courier New', monospace";
  const C = { ink: '#2b2420', pale: '#b3a78f', soft: '#6b6152', brick: '#9e2b25', hi: '#e2cf96', band: '#f6efdc', track: '#cbbfa6' };
  const LAST = 'FL';                                   // the state that takes Lantern over 270 on the last chorus
  const GEOM = {
    BAND_Y: 836, CUR_Y: 930, NEXT_Y: 1010,
    tally: { x: 960, y: 220, w: 1400, h: 84 },
    map: { x: 160, y: 282, w: 1600, h: 500 }, noteY: 804,
    dock: { tally: { x: 960, y: 196, w: 1560, h: 92 }, map: { x: 90, y: 268, w: 1740, h: 720 }, noteY: 1028 },
  };

  /* ---------------- the song: song_words.json ---------------- */
  class Song {
    constructor(d) {
      this.d = d;
      this.lines = d.lines.filter(l => l.words && l.words.length);
      // what the karaoke shows: the sheet's lines + the take's sung repeats (extras), in time order
      this.items = this.lines.map((l, i) => Object.assign({}, l, { i, kind: 'line' }))
        .concat((d.extras || []).filter(x => x.words && x.words.length).map((x, j) => Object.assign({}, x, { i: 'x' + j, kind: 'repeat' })))
        .sort((a, b) => a.s - b.s);
      this.dur = d.duration || this.lines[this.lines.length - 1].e + 2;
      const b = d.beats || {};
      this.bt = b.beat || 60 / (b.bpm || 112); this.off = b.offset || 0;
      this.sec = [];
      this.lines.forEach((l, i) => { const last = this.sec[this.sec.length - 1]; if (last && last.name === l.section) last.lines.push(i); else this.sec.push({ name: l.section, lines: [i] }); });
    }
    section(name, n = 1) { const s = this.sec.filter(x => x.name === name); const x = n < 0 ? s[s.length + n] : s[n - 1]; return x ? x.lines : []; }
    S(i) { return this.lines[i] ? this.lines[i].s : null; }
    E(i) { return this.lines[i] ? this.lines[i].e : null; }
  }

  /* ---------------- the plan: the race, from the model and the song's own lines / beat grid ---------------- */
  function rng(seed) { let s = seed >>> 0; return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296); }
  function plan(song) {
    const tie = College.tie(), fin = Object.assign({}, tie.assign);
    const need = College.need(2020), ev = st => College.ev(st, 2020);
    if (fin[LAST] !== 'anchor') throw new Error('plan: ' + LAST + ' is not an Anchor state of College.tie()');
    fin[LAST] = 'lantern';
    const lists = { lantern: [], anchor: [] };
    for (const st of Object.keys(fin).sort()) if (st !== LAST) lists[fin[st]].push(st);
    for (const k of ['lantern', 'anchor']) {                          // a fixed shuffle of each ticket's states
      const r = rng(k === 'lantern' ? 270 : 538), a = lists[k];
      for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
    }
    const order = [], sum = { lantern: 0, anchor: 0 };
    while (lists.lantern.length || lists.anchor.length) {          // the ticket behind goes next (a tie: alternate)
      const L_ = lists.lantern.length, A_ = lists.anchor.length;
      const k = !A_ ? 'lantern' : !L_ ? 'anchor' : sum.lantern < sum.anchor ? 'lantern' : sum.anchor < sum.lantern ? 'anchor'
        : order.length && order[order.length - 1].ink === 'lantern' ? 'anchor' : 'lantern';
      const st = lists[k].shift(); sum[k] += ev(st); order.push({ st, ink: k, ev: ev(st) });
    }
    // cues from the song: the first verse word; the last chorus's first word (Florida); that chorus's end (CERTIFIED)
    const v1 = song.section('Verse 1'), lastCh = song.section('Chorus', -1), firstCh = song.section('Chorus', 1), v2 = song.section('Verse 2');
    const tFL = song.lines[lastCh[0]].words[0].s;
    const tStamp = song.E(lastCh[lastCh.length - 1]) + 0.35;
    const bt = song.bt, off = song.off, tV = song.S(v1[0]);
    const b0 = Math.ceil((tV - off) / bt - 0.25);
    let t0 = off + b0 * bt, step = 2 * bt;
    if (t0 + step * (order.length - 1) > tFL - 1.5) step = (tFL - 1.5 - t0) / (order.length - 1);   // never needed by this take
    order.forEach((o, k) => { o.at = t0 + k * step; });
    order.push({ st: LAST, ink: 'lantern', ev: ev(LAST), at: tFL });
    // the cartogram windows (MORPH s in at `from`, MORPH s back, done by `to`)
    const windows = [];
    if (v1.length > 2 && firstCh.length) windows.push({ from: song.S(v1[2]), to: song.S(firstCh[0]), why: 'House seats, plus two' });
    if (v2.length > 2) windows.push({ from: song.S(v2[0]), to: song.S(v2[2]), why: 'the census' });
    const final = { lantern: sum.lantern + ev(LAST), anchor: sum.anchor };
    return { order, tFL, tStamp, windows, final, need, before: { lantern: sum.lantern, anchor: sum.anchor }, step };
  }
  const easeIO = x => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);
  const c01 = x => (x < 0 ? 0 : x > 1 ? 1 : x);
  /* the picture at song time t: how many states are inked, the counter (rolled in song time), the morph, the stamp */
  function pictureAt(pl, t) {
    let k = 0; const shown = { lantern: 0, anchor: 0 }, sum = { lantern: 0, anchor: 0 };
    for (const o of pl.order) {
      if (t < o.at) break;
      k++; sum[o.ink] += o.ev; shown[o.ink] += o.ev * E.outCubic(c01((t - o.at) / ROLL));
    }
    let u = 0;
    for (const w of pl.windows) u += easeIO(c01((t - w.from) / MORPH)) - easeIO(c01((t - (w.to - MORPH)) / MORPH));
    u = c01(u);
    const winner = sum.lantern >= pl.need ? 'lantern' : sum.anchor >= pl.need ? 'anchor' : null;
    return { k, lantern: sum.lantern, anchor: sum.anchor, shown, u, stamp: t >= pl.tStamp ? c01((t - pl.tStamp) / 0.3) : 0, winner };
  }

  /* the CERTIFIED stamp, baked once (2× scale) on its own canvas: the stamp's worn-ink specks then knock holes in the
     stamp only, not in the inked map under it */
  const stampWord = () => (Game.lesson && Game.lesson.completeCard && Game.lesson.completeCard.title) || 'CERTIFIED';
  let STAMP_SPRITE = null;
  function stampSprite() {
    if (STAMP_SPRITE) return STAMP_SPRITE;
    if (!(document.fonts && document.fonts.check("84px 'Special Elite'"))) return null;
    const cv = document.createElement('canvas'); cv.width = 1400; cv.height = 520;
    const keep = g;
    try { g = cv.getContext('2d'); g.translate(700, 260); g.scale(2, 2); UI.stamp(stampWord(), 0, 0, { rot: -0.1, s: 1, color: C.brick }); }
    finally { g = keep; }
    return (STAMP_SPRITE = cv);
  }

  /* ---------------- karaoke (only the words of song_words.json) ---------------- */
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9]/g, '');
  function hookIdx(line) {                            // the words "two seventy" (the hook's number) in any line
    const out = new Set(), w = line.words;
    for (let j = 0; j + 1 < w.length; j++) if (normW(w[j].w) === 'two' && normW(w[j + 1].w) === 'seventy') { out.add(j); out.add(j + 1); }
    return out;
  }
  const wordEnd = (ws, j) => { const nx = ws[j + 1]; return Math.min(Math.max(ws[j].e, ws[j].s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); };
  /* a line comes up 0.5 s before its first word, never before the previous line has been sung; it STAYS up through a
     gap (the take's unsheeted half chorus keeps the last chorus line on screen until the outro comes up) */
  const appearAt = (L, k) => (k === 0 ? L[0].s - 0.5 : Math.min(L[k].s, Math.max(L[k].s - 0.5, L[k - 1].e + 0.05)));
  function lyricAt(song, t) {
    const L = song.items; let cur = -1;
    for (let k = 0; k < L.length; k++) if (t >= appearAt(L, k)) cur = k;
    const c = cur >= 0 ? L[cur] : null, nx = L[cur + 1] || null;
    return { cur: c, idx: c ? c.i : null, appear: cur >= 0 ? appearAt(L, cur) : null, next: nx && nx.s - t < 8 ? nx : null };
  }
  function activeWord(song, t) {
    const { cur, idx } = lyricAt(song, t); if (!cur) return null;
    const ws = cur.words;
    for (let j = 0; j < ws.length; j++) if (t >= ws[j].s - 0.02 && t < wordEnd(ws, j)) return { line: idx, j, w: ws[j].w, s: ws[j].s, e: ws[j].e };
    return null;
  }
  const tw = (s, font) => { g.font = font; return g.measureText(s).width; };
  function fit(text, x, y, size, maxW, o = {}) {
    let s = size; const f = o.font || TYPE;
    const w = tw(text, `${s}px ${f}`); if (w > maxW) s = Math.floor(s * maxW / w);
    g.save(); g.font = `${s}px ${f}`; g.textAlign = o.align || 'center'; g.textBaseline = 'middle'; g.fillStyle = o.color || C.ink;
    if (o.alpha != null) g.globalAlpha *= o.alpha;
    g.fillText(text, x, y); g.restore();
    return s;
  }
  function drawCurrent(line, t, y, appear) {
    let size = Math.round(UI.text(58));
    const font = s => `${s}px ${WOOD}`, hk = hookIdx(line);
    let sp = tw(' ', font(size)), ws = line.words.map(w => tw(w.w, font(size)));
    let tot = ws.reduce((a, b) => a + b, 0) + sp * (ws.length - 1);
    const maxW = 1760;
    if (tot > maxW) { const k = maxW / tot; size = Math.floor(size * k); sp *= k; ws = ws.map(w => w * k); tot *= k; }
    const a = E.outCubic(c01((t - appear) / 0.25)), yy = y + (1 - a) * 24;
    let x = 960 - tot / 2;
    g.save(); g.globalAlpha *= a; g.textBaseline = 'middle'; g.textAlign = 'left'; g.font = font(size);
    line.words.forEach((w, j) => {
      const sung = t >= w.s - 0.02, active = sung && t < wordEnd(line.words, j);
      if (active) { g.fillStyle = C.hi; g.fillRect(x - 8, yy - size * 0.62, ws[j] + 16, size * 1.22); g.strokeStyle = C.ink; g.lineWidth = 2; g.strokeRect(x - 8, yy - size * 0.62, ws[j] + 16, size * 1.22); }
      g.fillStyle = !sung ? C.pale : hk.has(j) ? C.brick : C.ink;
      g.fillText(w.w, x, yy + size * 0.04);
      x += ws[j] + sp;
    });
    g.restore();
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class SongScene extends Scene {
    enter() {
      this.song = null; this.plan = null; this.err = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false;
      this.pos = 0; this.t0ctx = null; this.heard = 0;
      this.audioReady = false; this.completed = false; this.gone = false;
      this.k = -1; this.stampSnd = false;
      Audio.stopMusic();
      this.map = MapKit.map({ box: GEOM.map, strip: 'off', picker: false, keyboard: false, labels: true, ev: true, callouts: true });
      MapKit.load().then(() => { if (this.map && !this.gone) this.map.prewarm(); });
      const wurl = Assets.url(WORDS);
      const words = (window.Loader ? Loader.json(wurl).then(d => d || Promise.reject(new Error('no timings'))) : fetch(wurl).then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))))
        .then(d => { if (!d || !Array.isArray(d.lines) || !d.lines.length) throw new Error('empty timings'); const S = new Song(d); this.plan = plan(S); this.song = S; })
        .catch(e => { this.err = String(e && e.message); console.warn('[song] no timings', this.err); });
      const audio = (Audio.has(KEY) ? Promise.resolve() : Audio.load({ [KEY]: Assets.url(AUDIO) })).then(() => { this.audioReady = Audio.has(KEY); }, e => { console.warn('[song] no audio', e && e.message); });
      Promise.all([words, audio]).then(() => { if (!this.gone && !this.frozen && !this.startedOnce && this.song) this.play(0); });
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    exit() {
      this.gone = true; this.playing = false; Audio.stopGroup('song');
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }
    get key() { return KEY; }
    back() { Game.replace(new Levels.map()); }
    /* the song clock: the <audio> element while it plays (t0ctx != null = "on the audio clock"), else pos */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el && m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false;
      from = clamp(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('song');
      this.pos = from; this.playing = true; this.t0ctx = null;
      if (from < this.plan.tStamp) this.stampSnd = false;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(KEY);
      if (this.media) {
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; } else if (!ok) { this.pause(); this.blocked = true; }
        });
      } else if (Audio.running()) {
        const at = Audio.now() + 0.06;
        Audio.schedule(KEY, { at, offset: from, gain: 1, group: 'song', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() {
      this.pos = this.now(); this.playing = false; this.t0ctx = null; this.playTok = (this.playTok || 0) + 1;
      Audio.stopGroup('song');
      if (this.media) this.media.pause();
    }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = clamp(t, 0, this.song.dur - 0.05); this.ended = false; if (this.pos < this.plan.tStamp) this.stampSnd = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    /* QA: freeze on song time t (no pause overlay) */
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; }
    /* stamp the step now (QA / integration; ≥ 90% heard does this at the end, or the Finish button) */
    done() { if (this.completed) return; this.completed = true; this.pause(); if (!Game.progress.stamps[STEP]) Game.complete(STEP); }
    canFinish() { return !this.completed && (this.song ? this.heard >= FINISH_AT * this.song.dur : !!this.err); }
    finish() { if (this.canFinish()) { Audio.play('stamp', { gain: 0.6 }); this.done(); } }
    lyricAt(t) { return this.song ? lyricAt(this.song, t) : null; }
    activeWord(t) { return this.song ? activeWord(this.song, t) : null; }
    pictureAt(t) { return this.plan ? pictureAt(this.plan, t) : null; }

    update(dt) {
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) &&
          (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      const t = this.now();
      if (!this.stampSnd && t >= this.plan.tStamp && t < this.plan.tStamp + 0.5) { this.stampSnd = true; if (!(window.__timeScale > 1)) Audio.play('stamp', { gain: 0.45 }); }
      if (t >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard >= FINISH_AT * this.song.dur) this.done();
      }
    }
    keyDown(k) {
      if (!this.song) { if (k === 'Enter') this.finish(); return; }
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter') { if (this.ended) this.back(); else this.finish(); }
    }
    stripZone() { return Math.max(32, UI.tap(32)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }
      if (UI.inHud(x, y, 3)) return;
      if (this.canFinish() && this._finBox && Math.abs(x - this._finBox.x) < this._finBox.w / 2 && Math.abs(y - this._finBox.y) < this._finBox.h / 2) return;
      if (y > 1080 - this.stripZone()) {
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); }
    pointerUp() {
      if (!this.scrub) return;
      const was = this.scrub.was; this.scrub = null;
      if (this.pos < this.plan.tStamp) this.stampSnd = false;
      if (was) this.play(this.pos);
    }

    /* ---------- the picture: the race on the map, the brass counter ---------- */
    picture(t, docked) {
      const G = docked ? GEOM.dock : GEOM, p = pictureAt(this.plan, t), m = this.map;
      // the map's inks: brought up to the k-th state only when k changes (the newest one lifts while playing)
      if (p.k !== this.k) {
        const A = {}; for (let i = 0; i < p.k; i++) A[this.plan.order[i].st] = this.plan.order[i].ink;
        const nw = p.k > 0 && p.k > this.k && this.playing ? this.plan.order[p.k - 1].st : null;
        m.setAll(A, nw ? { press: nw } : {});
        if (!nw) m.pressed = null;
        this.k = p.k;
      }
      m.morph = { from: p.u, to: p.u, t0: 0, dur: 1 };
      // the last state still open (Lantern one short): ringed until it is inked on the last chorus
      const lastO = this.plan.order[this.plan.order.length - 1];
      m.rings = p.k === this.plan.order.length - 1 ? { [lastO.st]: C.brick } : {};
      if (m.o.box !== G.map) m.o.box = G.map;
      MapKit.tally(G.tally.x, G.tally.y, G.tally.w, { lantern: p.shown.lantern, anchor: p.shown.anchor, total: College.total(2020), need: this.plan.need }, { h: G.tally.h });
      m.draw();
      fit(p.u > 0.5 ? NOTE_TILES : NOTE, 960, G.noteY, Math.round(docked ? 30 : UI.text(24)), 1700, { font: TYPE, color: C.soft });
      if (p.stamp > 0) {
        const L = m.layout(), mx = L.map.x + L.map.w * 0.3, my = L.map.y + L.map.h * 0.5;    // over the winner's (Lantern's) west
        const s = (docked ? 1.0 : 0.85) * (1 + 0.6 * (1 - E.outCubic(p.stamp)));
        g.save(); g.globalAlpha *= c01(p.stamp * 2);
        const sp = stampSprite();
        if (sp) { g.translate(mx, my); g.scale(s / 2, s / 2); g.drawImage(sp, -sp.width / 2, -sp.height / 2); }
        else UI.stamp(stampWord(), mx, my, { rot: -0.1, s, color: C.brick });
        g.restore();
      }
      this._dbg = { k: p.k, u: p.u, lantern: p.lantern, anchor: p.anchor };
      return p;
    }
    /* ---------- the karaoke band: a galley proof under the map ---------- */
    band(t) {
      const S = this.song, { BAND_Y, CUR_Y, NEXT_Y } = GEOM;
      paperRect(960, (BAND_Y + 1080) / 2 + 6, 1960, 1080 - BAND_Y + 12, C.band, { seed: 2701, depth: 1.2, boil: false });
      g.save(); g.strokeStyle = 'rgba(43,36,32,0.35)'; g.lineWidth = 2; g.beginPath(); g.moveTo(40, BAND_Y + 14); g.lineTo(1880, BAND_Y + 14); g.stroke(); g.restore();
      const { cur, next, appear } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      if (sec) fit(sec.toUpperCase(), 60, BAND_Y + 40, Math.round(UI.text(22)), 400, { font: TYPE, color: C.brick, align: 'left' });
      if (cur) drawCurrent(cur, t, CUR_Y, appear);
      if (next) fit(next.text, 960, NEXT_Y, Math.round(UI.text(34)), 1700, { font: TYPE, color: C.soft });
    }
    /* the lyric sheet (no timings): content/song.json, two columns */
    sheet() {
      const Sg = Game.content.song, lines = Sg && Sg.lyrics ? Sg.lyrics.split('\n').map(l => l.trim()).filter(l => l && !/^\[/.test(l)) : [];
      const per = Math.ceil(lines.length / 2), fs = Math.round(UI.text(28));
      lines.forEach((l, i) => fit(l, 160 + Math.floor(i / per) * 840, 300 + (i % per) * fs * 1.5, fs, 800, { font: TYPE, align: 'left', color: /two seventy/i.test(l) ? C.brick : C.ink }));
    }
    head(t0) {
      const title = ((Game.content.song && Game.content.song.title) || 'Two Seventy').toUpperCase();
      Civic.woodtype(title, 60, 84, 58, { color: '#f3ead7', ink: C.ink, t0: -1e6, seed: 2700, align: 'left', stagger: 0 });
      if (!UI.docked()) fit(CREDIT, 62, 148, Math.round(UI.text(24)), 900, { font: TYPE, color: C.soft, align: 'left' });   // (docked: the credit is in the dock)
    }

    ground() {
      g.fillStyle = paperTile('#ece3cf', 3, 'tex_paper'); g.fillRect(0, 0, Game.W, Game.H);
      if (IMG.tex_news) texScrap('tex_news', 1480, 120, 420, 180, { seed: 2702, rot: 0.04, depth: 0.6, alpha: 0.35 });
    }
    draw() {
      const docked = UI.docked(), gT = T, gBF = BF;
      if (!this.song || !MapKit.ready()) {
        this.ground();
        this.head();
        const msg = this.err ? 'The song is missing. Here are the words.' : 'tuning the brass band…';
        if (this.err) this.sheet(); else fit(msg, 960, 540, 44, 1400, { font: TYPE, color: C.soft });
        if (docked) { Dock.head((Game.content.song && Game.content.song.title) || 'Two Seventy', ''); Dock.text('sg-wait', [msg], { cls: 'note center' }); if (this.canFinish()) Dock.buttons('sg-fin', [{ label: 'Finish', onClick: () => this.finish() }]); this.dockBar(); }
        else this.chrome(null);
        return;
      }
      const t = clamp(this.now(), 0, this.song.dur);
      T = t; BF = Math.floor(t * 8);
      try {
        this.ground();
        this.head();
        this.picture(t, docked);
        if (!docked) this.band(t);
      } finally { T = gT; BF = gBF; }
      if (docked) this.dock(t); else this.chrome(t);
    }

    /* ---------- the portrait dock: karaoke, controls and the strip below the stage ---------- */
    dock(t) {
      const S = this.song, title = (Game.content.song && Game.content.song.title) || 'Two Seventy';
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head(title, `${mmss(t)} / ${mmss(S.dur)}`);
      const { cur, next, idx } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      if (cur) {
        const key = idx + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) { const hk = hookIdx(cur); this._dk = { key, words: cur.words.map((w, j) => ({ w: w.w, s: w.s, e: w.e, hook: hk.has(j) })) }; }
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: '', note: this.startedOnce || this.frozen ? '♪ ♫ ♪' : 'loading the song…' });
      Dock.text('credit', ['♪ ' + CREDIT], { cls: 'small center' });
      if (this.canFinish() && !this.ended) Dock.buttons('sg-fin', [{ label: 'Finish', color: C.brick, onClick: () => this.finish() }]);
      if (this.ended && Game.current() === this) Dock.buttons('sg-end', [{ label: 'Play it again', onClick: () => this.restart() }, { label: 'Back to the map', color: C.ink, onClick: () => this.back() }]);
      Dock.strip({ value: t, total: S.dur, ticks: this.ticks(), label: 'song position', onSeek: (v, phase) => this.scrubTo(v, phase) });
      this.dockBar();
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) this.pausedMark(540, 150);
    }
    dockBar() {
      if (this.song && !this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the song from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => this.back() });
    }
    ticks() { return this._ticks || (this._ticks = this.song.sec.map(s => this.song.S(s.lines[0])).filter(x => x > 0.5)); }
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = clamp(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (this.pos < this.plan.tStamp) this.stampSnd = false; if (was) this.play(this.pos); }
    }
    /* the paused mark: a wash and an ink disc with a triangle */
    pausedMark(y, r) {
      g.save(); g.fillStyle = 'rgba(243,236,217,0.55)'; g.fillRect(0, 0, 1920, UI.docked() ? 1080 : GEOM.BAND_Y); g.restore();
      g.save(); g.fillStyle = C.ink; g.beginPath(); g.arc(960, y, r, 0, Math.PI * 2); g.fill();
      g.fillStyle = '#f3ead7'; g.beginPath(); g.moveTo(960 - r * 0.3, y - r * 0.52); g.lineTo(960 + r * 0.52, y); g.lineTo(960 - r * 0.3, y + r * 0.52); g.closePath(); g.fill(); g.restore();
    }

    chrome(t) {
      const S = this.song;
      const m = UI.hudSlot(0), r = UI.hudSlot(1), pl = UI.hudSlot(2), fx = { r: m.r, fixed: true };
      if (UI.iconButton('sg-map', '⌂', m.x, m.y, fx)) { this.back(); return; }
      if (S) {
        if (UI.iconButton('sg-restart', '↺', r.x, r.y, fx)) this.restart();
        if (UI.iconButton('sg-play', this.playing ? '❚❚' : '▶', pl.x, pl.y, { ...fx, primary: true })) this.toggle();
      }
      this._finBox = null;
      if (this.canFinish() && !this.ended) {
        // on the HUD's row, left of ⌂ ↺ ▶ (the counter sits right under the HUD)
        const w = UI.tap(250), h = Math.min(UI.tap(84), 2 * m.r), fy = m.y, fxp = UI.hudLeft(3) - 24 - w / 2;
        this._finBox = { x: fxp, y: fy, w, h };
        if (UI.button('sg-finish', 'Finish', fxp, fy, { w, h, size: UI.text(40), color: C.brick })) { this.finish(); return; }
      }
      if (t == null) return;
      const zone = this.stripZone(), touch = zone > 32;
      if (!this.ended) UI.target('sg-strip', 960, 1080 - zone / 2, 1920, zone);
      const sh = touch ? 18 : 10, sy = 1080 - sh;
      g.save();
      g.fillStyle = C.track; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = C.ink; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = C.brick; this.ticks().forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      if (touch || this.scrub) { const kx = clamp(1920 * t / S.dur, 14, 1906); g.fillStyle = C.ink; g.beginPath(); g.arc(kx, sy + sh / 2, touch ? 22 : 14, 0, Math.PI * 2); g.fill(); }
      g.restore();
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) {
        this.pausedMark(470, 100);
        fit(UI.hint('paused · click or Space to play', 'paused · tap to play'), 960, 630, Math.round(UI.text(40)), 1400, { font: TYPE, color: C.ink });
      }
      if (this.ended && Game.current() === this) {
        g.save(); g.fillStyle = 'rgba(243,236,217,0.7)'; g.fillRect(0, 400, 1920, 300); g.restore();
        if (UI.button('sg-again', 'Play it again', 700, 550, { w: UI.tap(440), h: UI.tap(110), size: UI.text(46), color: '#d8c79a', text: C.ink })) this.restart();
        if (UI.button('sg-back', 'Back to the map', 1220, 550, { w: UI.tap(440), h: UI.tap(110), size: UI.text(46), color: C.brick })) this.back();
      }
    }
  }
  SongScene.STEP = STEP;
  SongScene.KEY = KEY;
  SongScene.Song = Song;
  SongScene.plan = plan;
  SongScene.pictureAt = pictureAt;
  SongScene.GEOM = GEOM;
  Levels.song = SongScene;
})();
